/**
 * Hooks for QMS Policy (Phase 1): policy documents, structure tree, requirements.
 */

import { useState, useEffect, useCallback } from 'react';
import qmsPolicyService from '../services/qmsPolicyService';

export function usePolicyDocuments(filters = {}) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await qmsPolicyService.getPolicyDocuments(filters);
      setDocuments(data);
    } catch (err) {
      console.error('usePolicyDocuments:', err);
      setError(err.message);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [JSON.stringify(filters)]);

  useEffect(() => {
    load();
  }, [load]);

  return { documents, loading, error, refresh: load };
}

export function usePolicyDocument(documentId) {
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!documentId) {
      setDocument(null);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await qmsPolicyService.getPolicyDocument(documentId);
      setDocument(data);
    } catch (err) {
      console.error('usePolicyDocument:', err);
      setError(err.message);
      setDocument(null);
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    load();
  }, [load]);

  return { document, loading, error, refresh: load };
}

export function usePolicyStructureTree(policyDocumentId) {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!policyDocumentId) {
      setTree([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await qmsPolicyService.getStructureTree(policyDocumentId);
      setTree(data);
    } catch (err) {
      console.error('usePolicyStructureTree:', err);
      setError(err.message);
      setTree([]);
    } finally {
      setLoading(false);
    }
  }, [policyDocumentId]);

  useEffect(() => {
    load();
  }, [load]);

  return { tree, loading, error, refresh: load };
}

export function useRequirementsByPolicy(policyDocumentId) {
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!policyDocumentId) {
      setRequirements([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await qmsPolicyService.getRequirementsByPolicy(policyDocumentId);
      setRequirements(data);
    } catch (err) {
      console.error('useRequirementsByPolicy:', err);
      setError(err.message);
      setRequirements([]);
    } finally {
      setLoading(false);
    }
  }, [policyDocumentId]);

  useEffect(() => {
    load();
  }, [load]);

  return { requirements, loading, error, refresh: load };
}

export function useRequirementsBySourceNode(sourcePolicyStructureId) {
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!sourcePolicyStructureId) {
      setRequirements([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await qmsPolicyService.getRequirementsBySourceNode(sourcePolicyStructureId);
      setRequirements(data);
    } catch (err) {
      console.error('useRequirementsBySourceNode:', err);
      setError(err.message);
      setRequirements([]);
    } finally {
      setLoading(false);
    }
  }, [sourcePolicyStructureId]);

  useEffect(() => {
    load();
  }, [load]);

  return { requirements, loading, error, refresh: load };
}

export function useQmsPolicyMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createPolicyDocument = async (data, userId, userName) => {
    try {
      setLoading(true);
      setError(null);
      return await qmsPolicyService.createPolicyDocument(data, userId, userName);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updatePolicyDocument = async (documentId, updates, userId, userName) => {
    try {
      setLoading(true);
      setError(null);
      return await qmsPolicyService.updatePolicyDocument(documentId, updates, userId, userName);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createStructureNode = async (data, userId, userName) => {
    try {
      setLoading(true);
      setError(null);
      return await qmsPolicyService.createStructureNode(data, userId, userName);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateStructureNode = async (nodeId, updates, userId, userName) => {
    try {
      setLoading(true);
      setError(null);
      return await qmsPolicyService.updateStructureNode(nodeId, updates, userId, userName);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createRequirement = async (data, userId, userName) => {
    try {
      setLoading(true);
      setError(null);
      return await qmsPolicyService.createRequirement(data, userId, userName);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateRequirement = async (requirementId, updates, userId, userName) => {
    try {
      setLoading(true);
      setError(null);
      return await qmsPolicyService.updateRequirement(requirementId, updates, userId, userName);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    createPolicyDocument,
    updatePolicyDocument,
    createStructureNode,
    updateStructureNode,
    createRequirement,
    updateRequirement,
    loading,
    error,
  };
}
